import { FirebaseAnalytics } from "@capacitor-firebase/analytics";
import {
  FirebaseAuthentication,
  User,
} from "@capacitor-firebase/authentication";
import { JSX, PropsWithChildren, useEffect, useState } from "react";

import { FirebaseAuthContext, useFirebaseAuthReturn } from "~applications";
import { createContextProvider, LazyValue, lazyValue } from "~utils";

import { EmulatorConfig } from "./emulator-config";
import {
  sendPasswordResetEmail,
  signInWithEmail,
  signInWithGoogle,
  signUpWithEmail,
  signUpWithGoogle,
} from "./sign-in-functions";

export function AuthProvider({ children }: PropsWithChildren): JSX.Element {
  return (
    <>
      <EmulatorConfig />
      <ContextProvider>{children}</ContextProvider>
    </>
  );
}

const ContextProvider = createContextProvider(FirebaseAuthContext, useValue);

function useValue(): LazyValue<useFirebaseAuthReturn> {
  const user = useUser();

  useEffect(() => {
    if (user?.uid !== undefined)
      FirebaseAnalytics.setUserId({ userId: user.uid });
  }, [user?.uid]);

  useKeepAnonymousSignedIn();

  return user !== undefined ? getReturnValue(user) : initialValue;
}

function useUser(): User | undefined {
  const [user, setUser] = useState<User>();

  useEffect(() => {
    const listenerPromise = FirebaseAuthentication.addListener(
      "authStateChange",
      ({ user }) => setUser(user ?? undefined),
    );

    return () => {
      void listenerPromise.then((handle) => handle.remove());
    };
  }, []);

  // Linking a provider to the anonymous user keeps its uid, so `authStateChange`
  // never fires for it; the token change that follows is the only signal that the
  // user now has a name, an email and is no longer anonymous.
  useEffect(() => {
    const listenerPromise = FirebaseAuthentication.addListener(
      "idTokenChange",
      () => {
        void FirebaseAuthentication.getCurrentUser().then(({ user }) =>
          setUser(user ?? undefined),
        );
      },
    );

    return () => {
      void listenerPromise.then((handle) => handle.remove());
    };
  }, []);

  return user;
}

function useKeepAnonymousSignedIn(): void {
  const [error, setError] = useState<unknown>();

  useEffect(() => {
    const listenerPromise = FirebaseAuthentication.addListener(
      "authStateChange",
      ({ user }) => {
        if (user === null)
          FirebaseAuthentication.signInAnonymously().catch(
            (signInError: unknown) => setError(() => signInError),
          );
      },
    );

    return () => {
      void listenerPromise.then((handle) => handle.remove());
    };
  }, []);

  // Rethrown in render so the failure surfaces in the error boundary instead of
  // leaving consumers suspended forever on a user that will never arrive.
  if (error !== undefined) throw error;
}

function getReturnValue(user: User): useFirebaseAuthReturn {
  return {
    userId: user.uid,
    authenticated: !user.isAnonymous,
    name: user.displayName ?? undefined,
    email: user.email ?? undefined,
    photoUrl: user.photoUrl ?? undefined,
    signOut,
    signUpWithGoogle,
    signInWithGoogle,
    signUpWithEmail,
    signInWithEmail,
    sendPasswordResetEmail,
  };
}

const initialValue = lazyValue(() => listenForUser().then(getReturnValue));

function listenForUser(): Promise<User> {
  return new Promise<User>((resolve, reject) => {
    FirebaseAuthentication.addListener("authStateChange", ({ user }) => {
      if (user !== null) resolve(user);
    }).catch(reject);

    FirebaseAuthentication.getCurrentUser()
      .then(({ user }) => {
        if (user !== null) resolve(user);
      })
      .catch(reject);
  });
}

async function signOut(): Promise<void> {
  await FirebaseAuthentication.signOut();
}
